import { useCallback, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import NoDataText from '../../../components/NoDataText';
import Loader from '../../../components/Loader';
import ViewCommonButton from '../../../components/buttons/ViewCommonButton';
import useSnackBar from '../../../hooks/useSnackBar';
import { useHasPermission } from '../../../hooks/useHasPermission';
import Popup from '../../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../../constants/popupDescriptions';
import TruncatedText from '../../../components/TruncatedText';

import AddRemittanceRecord from './AddRemittanceRecord';

export interface RemittanceTemplateColumn {
  id: number;
  key: string;
  label: string;
  displayOrder: number;
  maxLength: number;
  isRequired: boolean;
  isEditable: boolean;
  binding: string;
}

export interface RemittanceLine {
  id?: number | string;
  sequence?: number;
  amount: number | null;
  values: Record<string, string>;
}

export interface RemittanceTemplate {
  id: number;
  name: string;
  version: number;
  passportTemplateId: string | null;
  isVerified: boolean;
  maxRows: number;
  isActive: boolean;
  columns: RemittanceTemplateColumn[];
}

interface RemittanceAccordionProps {
  remittanceContext?: any | null; // eslint-disable-line @typescript-eslint/no-explicit-any
  remittanceLines: RemittanceLine[];
  deductionAmount?: number | string;
  isLoading?: boolean;
  onChangeRemittanceLines: (lines: RemittanceLine[]) => void;
  remittanceTemplate?: RemittanceTemplate | null;
  prefetchedValues?: Record<string, string>;
  canUpdate?: boolean;
  onFormOpenChange?: (isOpen: boolean) => void;
}

type RemittanceFormValues = Record<string, string>;

const useStyles = makeStyles<{ isBalanced: boolean; isWarning: boolean; }>()((theme: Theme, { isBalanced, isWarning }) => ({
  accordionRoot: {
    backgroundColor: 'transparent',
    borderRadius: '16px',
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Inter',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,

    '&:before': {
      display: 'none',
    },
  },

  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    fontFamily: 'Inter',
    minHeight: '48px',
    padding: 0,
    flexDirection: 'row-reverse',

    '&.MuiAccordionSummary-root': {
      minHeight: '10px',
      padding: 0,
    },

    '&.Mui-expanded': {
      minHeight: '10px',
    },

    '& .MuiAccordionSummary-content': {
      '&.Mui-expanded': {
        margin: '1px 0',
      },
    },

    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '20px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
    },
  },

  accordionDetails: {
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    fontFamily: 'Inter',
    border: '1px solid #4a4e5d',
    borderRadius: '16px',
    marginTop: '10px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },

  pageContainer: {
    display: 'flex',
    width: '100%',
    margin: '15px 0',
    minWidth: 0,
  },

  formContainer: {
    display: 'flex',
    flexBasis: '1184px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,

    '@media (max-width: 900px)': {
      flexBasis: '100%',
    },
  },

  formInfoContainer: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    gap: '24px',

    '@media (max-width: 900px)': {
      flexDirection: 'column',
    },
  },

  formInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 900px)': {
      flexBasis: '416px',
      padding: '12px',
    },
  },

  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',

    '@media (max-width: 600px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },

  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },

  bodyText: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '24px',
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
  },

  tableWrapper: {
    overflow: 'hidden',
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    marginTop: '32px',
    width: '100%',
    maxWidth: '100%',
    position: 'relative',
    boxSizing: 'border-box',
  },

  tableContainer: {
    width: '100%',
    maxWidth: '100%',
    background: 'transparent',
    borderRadius: '8px',
    overflow: 'hidden',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Inter',
  },

  tableHeaderCell: {
    background: theme.palette.primary.light,

    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '16px 8px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
    },
  },

  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '16px 8px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      verticalAlign: 'top',
      boxSizing: 'border-box',
    },
  },

  warningMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '8px',
    gap: '12px',
    background: isBalanced
      ? '#4cae4c1a'
      : isWarning
        ? '#ffd9000f'
        : '#cb5d5d1a',
    width: '100%',
    marginTop: '15px',
    boxSizing: 'border-box',
    borderRadius: '10px',
    border: `1px solid ${isBalanced
      ? '#88B856'
      : isWarning
        ? '#FFD900'
        : '#FF8663'
      }`,
    wordBreak: 'break-word',
  },

  warningMessageText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '120%',
    color: isBalanced
      ? '#88B856'
      : isWarning
        ? '#FFD900'
        : '#FF8663',
    margin: '3px',
  },

  forceWarningContainer: {
    background: '#ffd9000f',
    border: '1px solid #FFD900',
  },

  forceWarningText: {
    color: '#FFD900',
  },

  actionIconsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'nowrap',
  },

  actionIconButton: {
    padding: 0,
    color: theme.palette.common.white,

    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
      opacity: 0.5,
    },

    '&:hover': {
      opacity: 0.8,
    },
  },

  actionIcon: {
    fontSize: '20px',
  },

  addRowButtonContainer: {
    width: '200px',
    marginTop: '32px',
  },

  newLineContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '16px',
    marginTop: '32px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    boxSizing: 'border-box',
  },

  newLineTitle: {
    fontFamily: 'Inter',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: '8px',
  },

  fieldContainer: {
    marginTop: '30px',
  },

  formActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '24px',
    width: '40%',
  },
}));

const RemittanceAccordion = ({
  remittanceContext,
  remittanceLines = [],
  deductionAmount = 0,
  isLoading,
  onChangeRemittanceLines,
  canUpdate,
  onFormOpenChange,
}: RemittanceAccordionProps) => {
  const { setAlert } = useSnackBar();

  const canCreate = useHasPermission('Payments', 'Create');
  const canEdit = useHasPermission('Payments', 'Edit');
  const canDelete = useHasPermission('Payments', 'Delete');

  const [isAddRowOpen, setIsAddRowOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<number | string | null>(
    null,
  );
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [deletingLineId, setDeletingLineId] = useState<number | string | null>(
    null,
  );

  const templateColumns = useMemo(
    () =>
      [...(remittanceContext?.remittanceTemplate?.columns ?? [])].sort(
        (firstColumn, secondColumn) =>
          firstColumn.displayOrder - secondColumn.displayOrder,
      ),
    [remittanceContext?.remittanceTemplate],
  );

  const maxRows =
    remittanceContext?.remittanceTemplate?.maxRows ?? 15;

  const amountColumn = useMemo(
    () =>
      templateColumns.find(
        (column) => column.binding === 'LINE_AMOUNT',
      ),
    [templateColumns],
  );

  const remittanceTotal = useMemo(
    () =>
      remittanceLines.reduce((total, line) => {
        if (
          typeof line.amount === 'number' &&
          Number.isFinite(line.amount)
        ) {
          return total + line.amount;
        }

        return total;
      }, 0),
    [remittanceLines],
  );

  const deductionAmountNumber = Number(deductionAmount ?? 0);
  const differenceAmount =
    remittanceTotal - deductionAmountNumber;

  const isBalanced =
    Math.round(remittanceTotal * 100) ===
    Math.round(deductionAmountNumber * 100);

  const isWarning =
    !remittanceContext?.remittanceTemplate;

  const styles = useStyles({
    isBalanced,
    isWarning,
  });

  const buildNewRowFormValues =
    useCallback((): RemittanceFormValues => {
      return templateColumns.reduce(
        (accumulator, column) => {
          accumulator[column.key] =
            column.binding === 'LINE_AMOUNT'
              ? ''
              : remittanceContext?.prefetchedValues[column.key] ?? '';

          return accumulator;
        },
        {} as RemittanceFormValues,
      );
    }, [
      remittanceContext?.prefetchedValues,
      templateColumns,
    ]);

  const balanceMessage = useMemo(() => {
    if (isBalanced) {
      return `Row amounts total ${numberToUSDCurrency(remittanceTotal)} of ${numberToUSDCurrency(deductionAmountNumber)} ✓`;
    }

    if (differenceAmount > 0) {
      return `Row amounts total ${numberToUSDCurrency(remittanceTotal)} - ${numberToUSDCurrency(Math.abs(differenceAmount))} over the deduction amount of ${numberToUSDCurrency(deductionAmountNumber)}.`;
    }

    return `Row amounts total ${numberToUSDCurrency(remittanceTotal)} - ${numberToUSDCurrency(Math.abs(differenceAmount))} short of the deduction amount of ${numberToUSDCurrency(deductionAmountNumber)}.`;
  }, [
    deductionAmountNumber,
    differenceAmount,
    isBalanced,
    remittanceTotal,
  ]);

  const handleOpenAddRow = () => {
    setEditingRowId(null);
    setIsAddRowOpen(true);
    onFormOpenChange?.(true);
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setIsAddRowOpen(false);
    onFormOpenChange?.(false);
  };

  const handleEdit = (line: RemittanceLine) => {
    setEditingRowId(line.id ?? null);
    setIsAddRowOpen(true);
    onFormOpenChange?.(true);
  };

  const handleConfirm = (values: RemittanceFormValues) => {
    const amountKey = amountColumn?.key;

    const rawAmountValue = amountKey
      ? String(values[amountKey] ?? '').trim()
      : '';

    const nextAmount =
      rawAmountValue === ''
        ? null
        : Number(rawAmountValue.replace(/[$,\s]/g, ''));

    const rowValues = templateColumns.reduce(
      (accumulator, column) => {
        if (column.binding === 'LINE_AMOUNT') {
          return accumulator;
        }

        accumulator[column.key] = String(
          values[column.key] ?? '',
        ).trim();

        return accumulator;
      },
      {} as Record<string, string>,
    );

    const isEditing = editingRowId !== null;

    const nextId =
      editingRowId ??
      Math.max(
        0,
        ...remittanceLines.map((line) =>
          Number(line.id ?? line.sequence ?? 0),
        ),
      ) + 1;

    const nextLine: RemittanceLine = {
      id: editingRowId ?? nextId,
      amount:
        rawAmountValue === ''
          ? null
          : Number.isFinite(nextAmount)
            ? nextAmount
            : null,
      values: rowValues,
    };

    const updatedLines = isEditing
      ? remittanceLines.map((line) =>
        (line.id ?? line.sequence ?? line.amount) ===
          editingRowId
          ? nextLine
          : line,
      )
      : [...remittanceLines, nextLine];

    onChangeRemittanceLines(updatedLines);

    setAlert((previousAlert) => ({
      ...previousAlert,
      message: isEditing
        ? 'Remittance line updated successfully.'
        : 'Remittance line added successfully.',
      type: 'success',
    }));

    handleCancel();
  };

  const handleOpenDeletePopup = (lineId: number | string) => {
    setDeletingLineId(lineId);
    setIsDeletePopupOpen(true);
  };

  const handleCloseDeletePopup = () => {
    setDeletingLineId(null);
    setIsDeletePopupOpen(false);
  };

  const handleDeleteLine = () => {
    if (deletingLineId === null) {
      return;
    }

    if (remittanceLines.length <= 1) {
      setAlert((previousAlert) => ({
        ...previousAlert,
        message: 'At least one remittance row must remain.',
        type: 'warning',
      }));

      handleCloseDeletePopup();
      return;
    }

    const filtered = remittanceLines.filter(
      (line) =>
        (line.id ?? line.sequence ?? line.amount) !==
        deletingLineId,
    );

    onChangeRemittanceLines(filtered);

    setAlert((previousAlert) => ({
      ...previousAlert,
      message: 'Remittance line deleted successfully.',
      type: 'success',
    }));

    if (editingRowId === deletingLineId) {
      handleCancel();
    }

    handleCloseDeletePopup();
  };

  const hasRows = remittanceLines.length > 0;
  const isActionDisabled =
    !!remittanceContext?.templateWarning;
  const isEditable = canUpdate ?? true;

  const remittanceFormDefaultValues =
    useMemo((): RemittanceFormValues => {
      if (editingRowId === null) {
        return buildNewRowFormValues();
      }

      const selectedLine = remittanceLines.find(
        (line) =>
          (line.id ?? line.sequence ?? line.amount) ===
          editingRowId,
      );

      if (!selectedLine) {
        return {};
      }

      return templateColumns.reduce(
        (accumulator, column) => {
          accumulator[column.key] =
            column.binding === 'LINE_AMOUNT'
              ? selectedLine.amount?.toString() ?? ''
              : selectedLine.values[column.key] ?? '';

          return accumulator;
        },
        {} as RemittanceFormValues,
      );
    }, [
      editingRowId,
      remittanceLines,
      templateColumns,
      buildNewRowFormValues,
    ]);

  const hasActionsColumn = canEdit || canDelete;
  const actionsColumnWidth = 80;

  const dataColumnWidth =
    templateColumns.length > 0
      ? hasActionsColumn
        ? `calc((100% - ${actionsColumnWidth}px) / ${templateColumns.length})`
        : `calc(100% / ${templateColumns.length})`
      : 'auto';

  if (isLoading) {
    return (
      <Box className={styles.classes.pageContainer}>
        <Box className={styles.classes.formContainer}>
          <Box className={styles.classes.formInfoContainer}>
            <Box className={styles.classes.formInnerWrapper}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="150px"
              >
                <Loader colorType="warning" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Accordion
        defaultExpanded
        className={styles.classes.accordionRoot}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          className={styles.classes.accordionSummary}
        >
          <Box display="flex" alignItems="center">
            <Typography>Remittance Advice</Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          className={styles.classes.accordionDetails}
        >
          <Box className={styles.classes.pageContainer}>
            <Box className={styles.classes.formContainer}>
              <Box className={styles.classes.formInfoContainer}>
                <Box className={styles.classes.formInnerWrapper}>
                  <Box className={styles.classes.headerContainer}>
                    <Typography className={styles.classes.bodyText}>
                      This is what prints on the advice mailed with the
                      check.
                    </Typography>

                    <Typography className={styles.classes.bodyText}>
                      Standard Remittance {remittanceLines.length} of{' '}
                      {maxRows}
                    </Typography>
                  </Box>

                  {!remittanceContext?.remittanceTemplate && (
                    <Box className={styles.classes.warningMessageContainer}>
                      <Typography className={styles.classes.warningMessageText}>
                        No remittance template found. A default format will be
                        used while creating Check.
                      </Typography>
                    </Box>
                  )}

                  {remittanceContext?.templateWarning && (
                    <Box
                      className={clsx(
                        styles.classes.warningMessageContainer,
                        styles.classes.forceWarningContainer,
                      )}
                    >
                      <Typography
                        className={clsx(
                          styles.classes.warningMessageText,
                          styles.classes.forceWarningText,
                        )}
                      >
                        {remittanceContext.templateWarning}
                      </Typography>
                    </Box>
                  )}

                  {remittanceContext && (
                    <>
                      {hasRows && (
                        <Box className={styles.classes.warningMessageContainer}>
                          <Typography className={styles.classes.warningMessageText}>
                            {balanceMessage}
                          </Typography>
                        </Box>
                      )}

                      <Box className={styles.classes.tableWrapper}>
                        {!hasRows ? (
                          <Box mb="50px">
                            <NoDataText marginTop="50px" />
                          </Box>
                        ) : (
                          <TableContainer
                            className={styles.classes.tableContainer}
                            component={Paper}
                            elevation={0}
                          >
                            <Table
                              size="small"
                              sx={{
                                width: '100%',
                                tableLayout: 'fixed',
                                borderCollapse: 'collapse',
                              }}
                            >
                              <TableHead
                                className={styles.classes.tableHeaderCell}
                              >
                                <TableRow>
                                  {templateColumns.map((column) => (
                                    <TableCell
                                      key={column.id}
                                      sx={{
                                        width: dataColumnWidth,
                                        maxWidth: dataColumnWidth,
                                      }}
                                    >
                                      <TruncatedText
                                        text={column.label}
                                        maxLength={30}
                                        sx={{
                                          width: '100%',
                                          minWidth: 0,
                                        }}
                                      />
                                    </TableCell>
                                  ))}

                                  {hasActionsColumn && (
                                    <TableCell
                                      sx={{
                                        width: `${actionsColumnWidth}px`,
                                        maxWidth: `${actionsColumnWidth}px`,
                                      }}
                                    >
                                      Actions
                                    </TableCell>
                                  )}
                                </TableRow>
                              </TableHead>

                              <TableBody
                                className={styles.classes.tableBodyCell}
                              >
                                {remittanceLines.map((line, index) => (
                                  <TableRow
                                    key={String(
                                      line.id ??
                                      line.sequence ??
                                      `remittance-line-${index}`,
                                    )}
                                  >
                                    {templateColumns.map((column) => {
                                      const cellValue =
                                        column.binding === 'LINE_AMOUNT'
                                          ? line.amount === null ||
                                            line.amount === undefined
                                            ? '-'
                                            : numberToUSDCurrency(line.amount)
                                          : line.values[column.key] || '-';

                                      return (
                                        <TableCell
                                          key={`${String(line.id ?? line.sequence ?? index)}-${column.id}`}
                                          sx={{ width: dataColumnWidth, maxWidth: dataColumnWidth }}
                                        >

                                          <TruncatedText
                                            text={cellValue}
                                            maxLength={30}
                                            sx={{
                                              width: '100%',
                                              minWidth: 0,
                                            }}
                                          />
                                        </TableCell>
                                      );
                                    })}

                                    {hasActionsColumn && (
                                      <TableCell
                                        sx={{
                                          width: `${actionsColumnWidth}px`,
                                          maxWidth: `${actionsColumnWidth}px`,
                                        }}
                                      >
                                        <Box
                                          className={
                                            styles.classes.actionIconsContainer
                                          }
                                        >
                                          {canEdit && (
                                            <IconButton
                                              className={
                                                styles.classes.actionIconButton
                                              }
                                              disabled={
                                                isActionDisabled ||
                                                !isEditable
                                              }
                                              aria-label="Edit remittance row"
                                              onClick={() =>
                                                !isActionDisabled &&
                                                handleEdit(line)
                                              }
                                            >
                                              <EditIcon
                                                className={
                                                  styles.classes.actionIcon
                                                }
                                              />
                                            </IconButton>
                                          )}

                                          {canDelete && (
                                            <IconButton
                                              className={styles.classes.actionIconButton}
                                              disabled={isActionDisabled || !isEditable}
                                              aria-label="Delete remittance row"
                                              onClick={() =>
                                                !isActionDisabled &&
                                                handleOpenDeletePopup(
                                                  line.id ?? line.sequence ?? line.amount ?? '',
                                                )
                                              }
                                            >
                                              <DeleteIcon className={styles.classes.actionIcon} />
                                            </IconButton>
                                          )}

                                        </Box>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>

                      {/* {!isAddRowOpen &&
                        canCreate &&
                        isEditable &&
                        remittanceContext?.remittanceTemplate &&
                        !remittanceContext.templateWarning && (
                          <Box
                            className={
                              styles.classes.addRowButtonContainer
                            }
                            onClick={handleOpenAddRow}
                          >
                            <ViewCommonButton type="addRow" />
                          </Box>
                        )} */}

                      {!isAddRowOpen &&
                        canCreate &&
                        isEditable &&
                        remittanceContext?.remittanceTemplate &&
                        !remittanceContext.templateWarning && (
                          <Box
                            className={styles.classes.addRowButtonContainer}
                            onClick={handleOpenAddRow}
                          >
                            <ViewCommonButton type="addRow" />
                          </Box>
                        )}

                      {isAddRowOpen && (
                        <AddRemittanceRecord
                          columns={templateColumns}
                          defaultValues={remittanceFormDefaultValues}
                          title={
                            editingRowId !== null
                              ? 'Edit Remittance Line'
                              : 'Add Remittance Line'
                          }
                          submitButtonText="Submit"
                          onCancel={handleCancel}
                          onSubmit={handleConfirm}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* <AddRemittanceRecord
        isOpen={isAddRowOpen}
        columns={templateColumns}
        defaultValues={remittanceFormDefaultValues}
        title={
          editingRowId !== null
            ? 'Edit Remittance Line'
            : 'Add Remittance Line'
        }
        submitButtonText="Submit"
        onCancel={handleCancel}
        onSubmit={handleConfirm}
      /> */}

      <Popup
        isOpen={isDeletePopupOpen}
        headlineText={
          POPUP_DESCRIPTIONS.deductionRemittanceDelete.headlineText
        }
        contentText={
          POPUP_DESCRIPTIONS.deductionRemittanceDelete.contentText
        }
        onMainButtonClick={handleDeleteLine}
        onSecondaryButtonClick={handleCloseDeletePopup}
      />
    </>
  );
};

export default RemittanceAccordion;
